import React from 'react';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import IconButton from '@material-ui/core/IconButton';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import Divider from '@material-ui/core/Divider';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';
import { agoDate, clockTime } from 'helpers/_date-helpers';
import { dateSettingsFormat, getTimeFormat } from 'common/dateFormatConfig';
import { makeStyles } from '@material-ui/core/styles';
import classnames from 'classnames';

import styles from './styles';
import { useSelector } from 'react-redux';
import useWorklogStyles from 'components/WorklogDetails/hooks/useWorklogStyles';

const useStyles = makeStyles(styles);

/**
 * HistoryDrawer acts as a nested drawer-like panel for submissions history.
 * - On desktop: panel appears to the right of the parent drawer (offset by parent width).
 * - On mobile (sm and below): panel covers the full viewport.
 * - This component does NOT use Material-UI Drawer, so the background remains clickable.
 *
 * @param {object} props
 * @param {boolean} props.open - Whether the panel is open
 * @param {function} props.onClose - Function to close the panel
 * @param {Array} props.submissions - Array of submission objects to display
 * @param {number} [props.parentDrawerWidth=768] - desktop-only offset from the right edge
 */
const HistoryDrawer = ({ open, onClose, submissions = [], parentDrawerWidth = 768, onHistorySubmissionSelect }) => {
  const classes = useStyles();
  const worklogClasses = useWorklogStyles();
  const { dateFormat, timeFormat } = useSelector(store => (store.personalProfile.organization?.settings || {}))
  const historyState = useSelector(store => store.currentWorklog?.worklogSheet?.historyState)

  // Sort submissions by date descending (latest first)
  const sortedSubmissions = submissions.slice().sort((a, b) => {
    if (!a.date || !b.date) return 0;
    return new Date(b.date) - new Date(a.date);
  });

  if (!open) return null;

  return (
    <div className={classes.container} style={{ right: parentDrawerWidth }}>
      <div role="presentation" className={classes.containerInner}>
        <div className={classes.headerRow}>
          <Typography variant="h6" className={classes.headerTitle}>
            History
          </Typography>
          <IconButton
            onClick={onClose}
            aria-label="Close"
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </div>
        <Divider className={classes.headerDivider} />
        <Typography
          variant="body1"
          color="textSecondary"
          className={classnames(worklogClasses.regularFontWeight, classes.subHeaderTitle)}
        >
          Submissions:
        </Typography>
        <List className={classes.list}>
          {sortedSubmissions.map((submission, i) => (
            <React.Fragment key={submission._id}>
              <ListItem
                button
                className={classnames(
                  classes.listItem,
                  submission?._id === historyState?.submissionId ? classes.listItemSelected : null,
                )}
                onClick={() => onHistorySubmissionSelect(submission._id)}
              >
                <ListItemText
                  className={classes.listItemText}
                  primary={
                    <>
                    {agoDate(
                      submission.startedAt,
                      null,
                      dateSettingsFormat(dateFormat).dayDateSettingsFormat
                    )}{' '}
                    at{' '}
                    {clockTime(
                      submission.startedAt,
                      getTimeFormat([], timeFormat, dateFormat)
                        .formatForTimePars
                    )}{' '}
                    by {submission.username}
                    </>
                  }
                  secondary={
                    submission.date
                      ? new Date(submission.date).toLocaleString()
                      : ''
                  }
                />
              </ListItem>
              {(i !== submissions?.length - 1) ? <Divider className={classes.listItemDivider} /> : null}
            </React.Fragment>
          ))}
          {sortedSubmissions.length === 0 && (
            <ListItem>
              <ListItemText primary="No submissions available." />
            </ListItem>
          )}
        </List>
      </div>
    </div>
  );
};

export default HistoryDrawer;
